// ============================================================================
// CrewAhead Maritime Platform - Files API Functions
// ============================================================================
// Document upload, delete, list, and pre-signed S3 URL generation.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DocumentFile {
  id: string;
  title: string;
  description: string | null;
  fileType: 'certificate' | 'document' | 'profile_image';
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  extension: string;
  uploadedAt: string;
  updatedAt: string;
}

export interface DocumentUploadInput {
  fileType: 'certificate' | 'document' | 'profile_image';
  file: File;
}

export interface PresignedUploadUrl {
  uploadUrl: string;
  fields: Record<string, string>;
  key: string;
}

// ─── List ────────────────────────────────────────────────────────────────────

/**
 * GET /documents/
 *
 * List all documents for the authenticated user.
 *
 * @returns List of document files
 */
export async function getDocuments(): Promise<DocumentFile[]> {
  try {
    const response = await apiClient.get<DocumentFile[]>(
      API_ENDPOINTS.FILES.DOCUMENTS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Create ──────────────────────────────────────────────────────────────────

/**
 * POST /documents/
 *
 * Create a new document record (with file reference).
 *
 * @param data - Document data
 * @returns Created document
 */
export async function createDocument(data: {
  title: string;
  description?: string;
  fileType: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
}): Promise<DocumentFile> {
  try {
    const response = await apiClient.post<DocumentFile>(
      API_ENDPOINTS.FILES.DOCUMENTS,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /documents/{id}/
 *
 * Retrieve a single document detail.
 *
 * @param id - Document ID
 * @returns Document detail
 */
export async function getDocument(id: string): Promise<DocumentFile> {
  try {
    const response = await apiClient.get<DocumentFile>(
      API_ENDPOINTS.FILES.DOCUMENT_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Delete ──────────────────────────────────────────────────────────────────

/**
 * DELETE /documents/{id}/
 *
 * Delete a document.
 *
 * @param id - Document ID to delete
 */
export async function deleteDocument(id: string): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.FILES.DOCUMENT_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Upload ──────────────────────────────────────────────────────────────────

/**
 * POST /documents/upload/
 *
 * Upload a document via multipart/form-data.
 *
 * @param data - Upload data with file
 * @returns Uploaded document record
 */
export async function uploadDocument(
  data: DocumentUploadInput
): Promise<DocumentFile> {
  try {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('file_type', data.fileType);

    const response = await apiClient.post<DocumentFile>(
      API_ENDPOINTS.FILES.UPLOAD,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Pre-signed URL ──────────────────────────────────────────────────────────

/**
 * GET /documents/upload-url/?filename={filename}&file_type={fileType}
 *
 * Get a pre-signed S3 POST URL for direct browser upload.
 *
 * @param filename - Name of the file to upload
 * @param fileType - Document type (certificate|document|profile_image)
 * @returns Pre-signed upload URL and fields
 */
export async function getPresignedUploadUrl(
  filename: string,
  fileType: 'certificate' | 'document' | 'profile_image'
): Promise<PresignedUploadUrl> {
  try {
    const response = await apiClient.get<PresignedUploadUrl>(
      `${API_ENDPOINTS.FILES.UPLOAD_URL}?filename=${encodeURIComponent(filename)}&file_type=${fileType}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
